import React from "react";
import { HubStatsTitle, HubStatsWrapper, GraphWrapper, GraphTitleValue } from "apps/BWW_PLAY_HUB/components/HubStats/styles";
import {
	HubStatsLegend
} from "apps/BWW_PLAY_HUB/components/HubStats/HubStatsLegend";
import {
	HubStatsDaysGraph
} from "apps/BWW_PLAY_HUB/components/HubStats/HubStatsDaysGraph";
import { EnumStatsType, getPercent } from "apps/BWW_PLAY_HUB/components/HubStats/utils";
import { useSelector } from "react-redux";
import { getHubStats } from "apps/BWW_PLAY_HUB/modules/selectors/hub_stats";
import {
	union,
	max,
	forEach,
	get,
	orderBy,
	size,
	filter
} from "lodash";
import { TGraphItems } from 'modules/types/advanced_stats';
import { SHORT_MONTH_NAMES } from 'modules/utils';


const legend_items = [
	{
		type: EnumStatsType.pp,
	},
	{
		type: EnumStatsType.trivia,
	},
];

const checkValues = (type: EnumStatsType, pp: Record<string, number>, trivia: Record<string, number>) => {
	const is_pp = type === EnumStatsType.pp;
	const is_trivia = type === EnumStatsType.trivia;

	if (!size(pp) && !size(trivia)) {
		return false;
	}

	if (is_pp && !size(pp)) {
		return false;
	}

	return !(is_trivia && !size(trivia));
}

interface IDailyRewardPoints {
	type: EnumStatsType;
}

export const DailyRewardPoints: React.FC<IDailyRewardPoints> = ({ type }) => {
	const { pp, trivia } = useSelector(getHubStats);
	const is_pp = type === EnumStatsType.pp;
	const is_trivia = type === EnumStatsType.trivia;

	const pp_daily_reward_points = pp.monthly_reward_points;
	const trivia_daily_reward_points = trivia.monthly_reward_points
	const reward_points = (is_pp) ? pp.reward_points : trivia.reward_points;

	const check = checkValues(type, pp_daily_reward_points, trivia_daily_reward_points);

	if (!check) {
		return null;
	}

	const pp_keys: string[] = [];
	const pp_values: number[] = [];
	const trivia_keys: string[] = [];
	const trivia_values: number[] = [];

	Object.entries(pp_daily_reward_points).forEach(([ key, value ]) => {
		pp_keys.push(key);
		pp_values.push(value);
	});

	Object.entries(trivia_daily_reward_points).forEach(([ key, value ]) => {
		trivia_keys.push(key);
		trivia_values.push(value);
	});

	const max_value = max([ ...pp_values, ...trivia_values ]) || 0;

	const union_keys = union(pp_keys, trivia_keys);
	const union_keys_sorted = [...union_keys].sort((a: string, b: string) => {
		return SHORT_MONTH_NAMES.indexOf(a) - SHORT_MONTH_NAMES.indexOf(b);
	});

	const days_graph_values: TGraphItems = {};

	forEach(union_keys_sorted, (value) => {
		const pp_value = get(pp_daily_reward_points, value, 0);
		const pp_percent = getPercent(pp_value, max_value);

		const trivia_value = get(trivia_daily_reward_points, value, 0);
		const trivia_percent = getPercent(trivia_value, max_value);

		const arr = [];

		if (is_pp) {
			arr.push({
				type: EnumStatsType.pp,
				value: pp_value,
				percent: pp_percent,
			});
		}

		if (is_trivia) {
			arr.push({
				type: EnumStatsType.trivia,
				value: trivia_value,
				percent: trivia_percent,
			});
		}

		days_graph_values[value] = orderBy(arr, ['value'], ['asc']);
	});

	const filtered_legend = filter(legend_items, { type });

	return (
		<HubStatsWrapper>
			<HubStatsTitle>Reward points won per month</HubStatsTitle>
			<GraphWrapper>
				<GraphTitleValue>{ reward_points }</GraphTitleValue>
				<HubStatsDaysGraph items={days_graph_values} format={null} />
			</GraphWrapper>
			<HubStatsLegend items={filtered_legend} show_value={false} show_percent={false} />
		</HubStatsWrapper>
	);
};
